/**
 * CANTED / BATTERED WALLS (freeform Phase 2 — docs/bim-plan/15, Matt's resolved §7):
 * walls whose faces slope inward going up — the Spanish battered hearth/chimney
 * vocabulary. The cant is specified per FACE as a TOP INSET in inches (§7 Q1 — the
 * dimension a section would carry; the angle derives), clamped to 30° from plumb
 * (§7 Q5 — the finish-classification contract's safe range) and to a sane minimum
 * top thickness.
 *
 * MUTUAL EXCLUSIONS LIVE IN THE PREDICATE (one place, consulted everywhere): a wall
 * with a footing/stem, attached top or base, or arced §8.12 terminations is NOT
 * canted — those derivations all assume one plan ring + a flat band, and the spike
 * resolved them as v1-exclusive with cant. Geometry-side guards (closed loops,
 * curved paths) stay in bim-geometry where the flattened path lives.
 */
import type { AssemblyRecord, ParamValue } from './types'

export const CANT_MAX_DEG = 30
const CANT_MAX_TAN = Math.tan((CANT_MAX_DEG * Math.PI) / 180)
/** the top of a battered wall never thins below this (ft) */
export const CANT_MIN_TOP_FT = 1 / 12

const num = (v: ParamValue | undefined): number => (typeof v === 'number' && v > 0 ? v : 0)

/**
 * Is this wall canted? Raw inset params > 0 AND none of the v1-exclusive modes
 * (footing/stem, top/base attach — legacy blanket topAttach included — and the
 * §8.12 arc terminations). A wall that trips an exclusion silently builds PLUMB —
 * the same fall-back-to-flat convention the arc terminations use.
 */
/** signed param read (the lean can go either way) */
const snum = (v: ParamValue | undefined): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)

export function isCantedWallParams(params: Record<string, ParamValue>): boolean {
  if (num(params.cantExtInsetIn) <= 0 && num(params.cantIntInsetIn) <= 0 && snum(params.cantLeanIn) === 0) return false
  if (params.footingEnabled === true || params.wallIsStem === true) return false
  const topIds = params.topAttachIds
  if (Array.isArray(topIds) && topIds.length) return false
  if (params.topAttach === 'roof') return false
  const baseIds = params.baseAttachIds
  if (Array.isArray(baseIds) && baseIds.length) return false
  const topP = params.topProfile, botP = params.bottomProfile
  if (typeof topP === 'string' && topP !== 'flat' && topP !== '') return false
  if (typeof botP === 'string' && botP !== 'flat' && botP !== '') return false
  return true
}

/**
 * The two face insets in FEET, clamped: each to height·tan(30°) (§7 Q5), and
 * together so the wall's top thickness never drops below CANT_MIN_TOP_FT
 * (proportional scale-down when both faces over-ask). ext = the assembly's
 * exterior face (layers run exterior→interior; `flipped` maps ext↔left elsewhere —
 * this function is side-agnostic).
 */
export function resolveCantInsetsFt(
  params: Record<string, ParamValue>,
  heightFt: number,
  thicknessFt: number,
): { extFt: number; intFt: number } {
  const maxByAngle = Math.max(heightFt, 0) * CANT_MAX_TAN
  let ext = Math.min(num(params.cantExtInsetIn) / 12, maxByAngle)
  let int_ = Math.min(num(params.cantIntInsetIn) / 12, maxByAngle)
  const maxTotal = Math.max(thicknessFt - CANT_MIN_TOP_FT, 0)
  const total = ext + int_
  if (total > maxTotal && total > 0) {
    const k = maxTotal / total
    ext *= k
    int_ *= k
  }
  return { extFt: ext, intFt: int_ }
}

/**
 * LEAN (2026-08-20, Matt's follow-up: "the walls are supposed to lean, right? Not
 * one side that leans in only?"): the whole-wall tilt — BOTH faces shift the same
 * top offset, thickness constant (ArchiCAD's "slanted" vs the insets' "double-
 * slanted" batter; the two COMPOSE). Signed: positive tips the top toward the
 * INTERIOR side. Clamped to 30° from plumb like the insets; unlike them it
 * consumes no core, so it never triggers the assembly clamp.
 */
export function resolveCantLeanFt(params: Record<string, ParamValue>, heightFt: number): number {
  const maxByAngle = Math.max(heightFt, 0) * CANT_MAX_TAN
  const lean = snum(params.cantLeanIn) / 12
  return Math.max(-maxByAngle, Math.min(maxByAngle, lean))
}

/**
 * Per-layer (o, z)-profile boundaries for the per-layer-sweep build (§7 Q4 — the
 * layered-poché architecture): finish layers ride their canted face at CONSTANT
 * thickness; the CORE absorbs the taper. Returns, for each assembly layer, its
 * inner/outer lateral offsets at the BASE and at the TOP (o toward the LEFT normal
 * of travel; exterior face at +t/2 when not flipped — the wallCoreLines
 * convention; `flipped` mirrors the finished bands). Degenerate layers (a core
 * squeezed below zero at the top) clamp — resolveCantInsetsFt's total clamp makes
 * real degeneracy impossible, this is belt and braces.
 */
export interface CantLayerBand {
  /** index into assembly.layers */
  layer: number
  /** lateral offsets at the base: [inner (right-ward), outer (left-ward)] */
  base: [number, number]
  /** lateral offsets at the top */
  top: [number, number]
}

export function cantLayerBands(
  assembly: AssemblyRecord | null | undefined,
  thicknessFt: number,
  extInsetFt: number,
  intInsetFt: number,
  flipped = false,
  /** whole-wall LEAN (ft, + toward interior) — shifts BOTH faces' tops equally */
  leanFt = 0,
): CantLayerBand[] {
  const t2 = thicknessFt / 2
  // ASSEMBLY-AWARE CLAMP (the half-inch-overrun bug, caught by the taper test):
  // the finish stacks keep CONSTANT thickness, so the insets must leave the CORE
  // at least CANT_MIN_TOP_FT wide at the top AFTER the finishes claim theirs —
  // resolveCantInsetsFt's plain thickness clamp can't know the stacks. Scale both
  // insets proportionally when they over-ask.
  if (assembly?.layers?.length) {
    const cs = assembly.coreBoundary?.start ?? 0
    const ce = assembly.coreBoundary?.end ?? assembly.layers.length - 1
    let stacks = 0
    for (let i = 0; i < assembly.layers.length; i++) {
      if (i < cs || i > ce) stacks += assembly.layers[i].thicknessFt
    }
    const maxTotal = Math.max(thicknessFt - stacks - CANT_MIN_TOP_FT, 0)
    const total = extInsetFt + intInsetFt
    if (total > maxTotal && total > 0) {
      const k = maxTotal / total
      extInsetFt *= k
      intInsetFt *= k
    }
  }
  // compute in "exterior at +t/2" space (the unflipped wallCoreLines convention);
  // a FLIPPED wall mirrors the finished bands at the end. The LEAN shifts both
  // faces' tops equally (toward interior = −o in this space); positive lean tips
  // toward the interior side.
  const leftTop = t2 - extInsetFt - leanFt
  const rightTop = -t2 + intInsetFt - leanFt
  const mirror = (bands: CantLayerBand[]): CantLayerBand[] =>
    !flipped ? bands : bands.map((b) => ({
      layer: b.layer,
      base: [-b.base[1], -b.base[0]] as [number, number],
      top: [-b.top[1], -b.top[0]] as [number, number],
    }))
  const layers = assembly?.layers?.length ? assembly.layers : null
  if (!layers) {
    return mirror([{ layer: 0, base: [-t2, t2], top: [rightTop, leftTop] }])
  }
  const coreStart = assembly!.coreBoundary?.start ?? 0
  const coreEnd = assembly!.coreBoundary?.end ?? layers.length - 1
  const out: CantLayerBand[] = []
  let cumBase = t2
  let cumTop = leftTop
  for (let i = 0; i < layers.length; i++) {
    const th = layers[i].thicknessFt
    const isCore = i >= coreStart && i <= coreEnd
    const outerBase = cumBase
    const outerTop = cumTop
    let innerBase: number
    let innerTop: number
    if (isCore && i === coreEnd) {
      // the LAST core layer absorbs the taper: its inner boundary is wherever the
      // interior-side stack (walked from the right face) puts it
      let cumInnerBase = -t2
      let cumInnerTop = rightTop
      for (let j = layers.length - 1; j > i; j--) {
        cumInnerBase += layers[j].thicknessFt
        cumInnerTop += layers[j].thicknessFt
      }
      innerBase = cumInnerBase
      innerTop = cumInnerTop
    } else {
      innerBase = outerBase - th
      innerTop = outerTop - th
    }
    // clamp any top-side inversion (belt and braces — see doc)
    if (innerTop > outerTop) innerTop = outerTop
    out.push({ layer: i, base: [innerBase, outerBase], top: [innerTop, outerTop] })
    cumBase = innerBase
    cumTop = innerTop
  }
  return mirror(out)
}
